import { useState } from "react";
import { Link } from "wouter";
import { useClerk } from "@clerk/react";
import * as Sentry from "@sentry/react";
import {
  deleteCurrentUser,
  useGetAccountDeletionPreview,
  type AccountDeletionIncomplete,
  type AccountDeletionPreview,
} from "@workspace/api-client-react";

// WI#34 — web account deletion (parity with the iPhone app, Apple 5.1.1(v)). The account menu opens
// this confirm step. Every notice here is server-authored (GET /me/deletion-preview), so the page
// never guesses the billing channel or who becomes captain. Deletion is never refused: the notices
// inform, and "Delete now" is always available. UX follows the approved iPhone prototype (pr33).

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

export type DeleteIssue = { message: string; periodEndNote?: string };

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

export function deletionIssueFrom(err: unknown): DeleteIssue {
  const body = (err as { data?: AccountDeletionIncomplete | null }).data;
  if (body?.code !== "deletion_incomplete") {
    return { message: "Couldn't finish deleting your account. Try again." };
  }
  const end = formatDate(body.currentPeriodEnd);
  return {
    message: body.error,
    periodEndNote: body.subscriptionCancellationScheduled
      ? end
        ? `Your subscription is set to end on ${end}. Try again to finish deleting your account.`
        : "Your subscription is set to end at the end of the billing period. Try again to finish deleting your account."
      : undefined,
  };
}

export function DeleteAccountConfirm({
  preview,
  busy,
  issue,
  onDelete,
}: {
  preview: AccountDeletionPreview;
  busy: boolean;
  issue: DeleteIssue | null;
  onDelete: () => void;
}) {
  return (
    <div className="max-w-lg">
      <h1 className="font-display text-2xl font-bold mb-2">Delete account?</h1>
      <p className="text-muted-foreground text-sm mb-6">Your profile and check-ins will be deleted.</p>

      <ul className="space-y-3 mb-6">
        {preview.webSubscription && (
          <li className="rounded border border-border px-4 py-3 text-sm">
            Your web subscription will be cancelled at period end. Your account will be deleted now.
          </li>
        )}
        {preview.appleSubscription && (
          <li className="rounded border border-border px-4 py-3 text-sm">
            <p>Your Apple subscription continues until you cancel it.</p>
            <a
              href={preview.appleSubscription.manageUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block underline"
            >
              Manage Apple subscription
            </a>
          </li>
        )}
        {preview.captainTransfers.map((t) => (
          <li key={t.squadId} className="rounded border border-border px-4 py-3 text-sm">
            <p className="font-semibold">Captain transfer</p>
            <p className="text-muted-foreground">
              {t.newCaptainName} becomes captain of {t.squadName}
            </p>
          </li>
        ))}
        {preview.squadsDeleted.map((s) => (
          <li key={s.squadId} className="rounded border border-border px-4 py-3 text-sm">
            Your solo squad, {s.squadName}, will also be deleted.
          </li>
        ))}
      </ul>

      {issue && (
        <div role="alert" className="mb-6 text-sm text-destructive">
          <p>{issue.message}</p>
          {issue.periodEndNote && <p className="mt-1">{issue.periodEndNote}</p>}
        </div>
      )}

      <div className="flex gap-3">
        <Link
          href="/dashboard"
          className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
        >
          Keep account
        </Link>
        <button
          type="button"
          onClick={onDelete}
          disabled={busy}
          className="flex-1 bg-destructive text-destructive-foreground rounded py-2 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {busy ? "Deleting..." : "Delete now"}
        </button>
      </div>
    </div>
  );
}

export default function DeleteAccount() {
  const { signOut } = useClerk();
  const preview = useGetAccountDeletionPreview();
  const [busy, setBusy] = useState(false);
  const [issue, setIssue] = useState<DeleteIssue | null>(null);

  async function handleDelete() {
    setBusy(true);
    setIssue(null);
    try {
      await deleteCurrentUser();
    } catch (err) {
      // The server already paged on its side; this records what the customer saw.
      Sentry.captureException(err, { tags: { flow: "account-deletion" } });
      setIssue(deletionIssueFrom(err));
      setBusy(false);
      return;
    }
    // 204: the account and its login are gone. End the session and land on sign-in with the
    // confirmation, rather than leaving a signed-in shell that can no longer load anything.
    await signOut({ redirectUrl: `${basePath}/sign-in?account=deleted` });
  }

  if (preview.isLoading) {
    return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading...</div>;
  }
  if (preview.isError || !preview.data) {
    return (
      <div role="alert" className="max-w-lg text-sm">
        <p className="mb-3">Couldn't load your account details. Try again.</p>
        <button type="button" onClick={() => preview.refetch()} className="underline">
          Try again
        </button>
      </div>
    );
  }
  return <DeleteAccountConfirm preview={preview.data} busy={busy} issue={issue} onDelete={handleDelete} />;
}
